/**
 * Listan över mina bokningar: aktuella och historik, som i bokningskortet.
 */

import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '@/services/api/client';
import { bookingApi } from '@/services/booking/bookingApi';
import { useLanguage, useTranslation } from '@/store/languageStore';
import {
  bookingDayBadge,
  sortBookings,
  upcomingBookings,
  type BookingListSort,
} from '@/utils/myBookingsWidget';

export interface MyBookingItem {
  id: string;
  customerName: string | null;
  startAt: string;
  endAt: string;
  status: string;
  pickupLocationName: string;
  pickupLocationId: string;
  vehicleLabel: string;
  tripPurpose?: string | null;
  bookingKind?: string | null;
  fuelType?: string | null;
  priorVehicleLabel?: string | null;
  awaitingVehicleAssignment?: boolean;
}

export interface BookingPeriodStats {
  drivenKm: number;
  estimatedKm: number;
  costKr: number | null;
}

export function useMyBookingsData() {
  return useQuery(
    ['dashboard-widget', '/api/dashboard/data/my-bookings'],
    async () => {
      const response = await api.get('/api/dashboard/data/my-bookings');
      if (response.data.success) {
        return {
          items: (response.data.data || []) as MyBookingItem[],
          history: (response.data.history || []) as MyBookingItem[],
          period: (response.data.period || { drivenKm: 0, estimatedKm: 0, costKr: null }) as BookingPeriodStats,
        };
      }
      throw new Error(response.data.error || 'Failed to load data');
    },
    { staleTime: 60 * 1000 }
  );
}

export function clock(iso: string, locale: string): string {
  return new Date(iso).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

function localYmd(iso: string): string {
  const date = new Date(iso);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export function DateBadge({
  iso,
  locale,
  todayLabel,
  timeZone,
  compact = false,
}: {
  iso: string;
  locale: string;
  todayLabel: string;
  timeZone: string;
  compact?: boolean;
}) {
  const badge = bookingDayBadge(iso, new Date(), locale, timeZone);
  return (
    <div className={`flex shrink-0 flex-col items-center justify-center border border-borderDefault bg-bgBase ${compact ? 'h-16 w-16 rounded-xl' : 'h-28 w-28 rounded-2xl'}`}>
      <span className={`font-semibold uppercase tracking-wide ${compact ? 'text-xs' : 'text-sm'} ${badge.today ? 'text-[#2dd4bf]' : 'text-textSecondary'}`}>
        {badge.today ? todayLabel : badge.weekday}
      </span>
      <span className={`font-semibold leading-none text-textDefault ${compact ? 'text-xl' : 'text-5xl'}`}>{badge.day}</span>
      <span className={`lowercase text-textSecondary ${compact ? 'text-xs' : 'mt-1 text-base'}`}>{badge.month}</span>
    </div>
  );
}

function BookingRow({
  item,
  locale,
  todayLabel,
  timeZone,
  vehicleName,
  statusLabel,
  editLabel,
  cancelLabel,
  cancelling,
  onOpen,
  onEdit,
  onCancel,
}: {
  item: MyBookingItem;
  locale: string;
  todayLabel: string;
  timeZone: string;
  vehicleName: (row: MyBookingItem) => string;
  statusLabel: (row: MyBookingItem) => string;
  editLabel: string;
  cancelLabel: string;
  cancelling: boolean;
  onOpen: (row: MyBookingItem) => void;
  onEdit: (row: MyBookingItem) => void;
  onCancel: (row: MyBookingItem) => void;
}) {
  const purpose = item.tripPurpose?.trim();
  const subtitle = [purpose, item.pickupLocationName].filter(Boolean).join(' · ');
  const canChange = item.status === 'BOOKED' && !item.awaitingVehicleAssignment;
  return (
    <div className="flex min-h-[4.5rem] w-full items-center gap-3 rounded-lg px-1 py-3">
      <button type="button" onClick={() => onOpen(item)} className="flex min-w-0 flex-1 items-center gap-3 text-left hover:bg-bgHover rounded-lg">
        <DateBadge iso={item.startAt} locale={locale} todayLabel={todayLabel} timeZone={timeZone} compact />
        <div className="min-w-0 flex-1">
          <div className="truncate text-lg font-semibold text-textDefault">{vehicleName(item)}</div>
          <div className="truncate text-sm text-textSecondary">{subtitle}</div>
        </div>
        <div className="whitespace-nowrap text-sm text-textSecondary">
          {clock(item.startAt, locale)}–{clock(item.endAt, locale)}
        </div>
        <span className="whitespace-nowrap rounded-full bg-[#134e4a] px-2.5 py-1 text-sm text-[#2dd4bf]">
          {statusLabel(item)}
        </span>
      </button>
      {canChange && (
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => onEdit(item)}
            className="rounded-xl border border-borderDefault px-3 py-1.5 text-sm font-medium text-textDefault hover:bg-bgHover"
          >
            {editLabel}
          </button>
          <button
            type="button"
            onClick={() => onCancel(item)}
            disabled={cancelling}
            className="rounded-xl border border-borderDefault px-3 py-1.5 text-sm font-medium text-red-400 hover:bg-bgHover disabled:opacity-50"
          >
            {cancelLabel}
          </button>
        </div>
      )}
    </div>
  );
}

function ListTab({
  active,
  label,
  count,
  onClick,
}: {
  active: boolean;
  label: string;
  count: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-sm ${active ? 'bg-bgElevated text-textDefault' : 'text-textSecondary'}`}
    >
      {label}
      <span className={`inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1 text-xs ${active ? 'bg-[#134e4a] text-[#2dd4bf]' : 'bg-bgBase text-textTertiary'}`}>
        {count}
      </span>
    </button>
  );
}

const MyBookingsList: React.FC<{ listClassName?: string }> = ({
  listClassName = 'min-h-0 flex-1 overflow-y-auto',
}) => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'current' | 'history'>('current');
  const [sort, setSort] = useState<BookingListSort>('start-asc');
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const locale = language === 'en' ? 'en-GB' : 'sv-SE';
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { data } = useMyBookingsData();
  const now = useMemo(() => new Date(), []);
  const bookings = useMemo(() => upcomingBookings(data?.items ?? [], now), [data, now]);
  const history = data?.history ?? [];
  const visible = useMemo(
    () => sortBookings(tab === 'current' ? bookings : history, sort),
    [tab, bookings, history, sort]
  );

  const openBooking = (item: MyBookingItem, mode: 'view' | 'edit' = 'view') => {
    const params = new URLSearchParams({
      pickupLocationId: String(item.pickupLocationId),
      date: localYmd(item.startAt),
      bookingId: String(item.id),
    });
    if (mode === 'edit') params.set('edit', '1');
    navigate(`/booking/calendar?${params.toString()}`);
  };

  const cancelBooking = async (item: MyBookingItem) => {
    if (item.status !== 'BOOKED' || item.awaitingVehicleAssignment) return;
    if (!window.confirm(t('booking.cancelBookingConfirm'))) return;
    setCancellingId(item.id);
    try {
      await bookingApi.selfCancelBooking(item.id);
      toast.success(t('booking.cancelBookingSuccess'));
      await queryClient.invalidateQueries(['dashboard-widget', '/api/dashboard/data/my-bookings']);
    } catch {
      toast.error(t('booking.cancelBookingError'));
    } finally {
      setCancellingId(null);
    }
  };

  const vehicleName = (item: MyBookingItem) =>
    item.awaitingVehicleAssignment ? t('bookingWidget.noVehicleAssigned') : item.vehicleLabel;

  const statusLabel = (item: MyBookingItem) => {
    if (item.awaitingVehicleAssignment) return t('bookingWidget.awaiting');
    if (item.status === 'RETURNED') return t('bookingWidget.returned');
    if (item.status === 'CHECKED_OUT') return t('bookingWidget.checkedOut');
    return t('bookingWidget.booked');
  };

  return (
    <div className="flex min-h-0 min-w-0 flex-col">
      <div className="mb-3 flex shrink-0 flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-full border border-borderDefault p-1">
          <ListTab active={tab === 'current'} label={t('bookingWidget.current')} count={bookings.length} onClick={() => setTab('current')} />
          <ListTab active={tab === 'history'} label={t('bookingWidget.history')} count={history.length} onClick={() => setTab('history')} />
        </div>
        <label className="flex items-center gap-2 text-sm text-textSecondary">
          {t('bookingWidget.sort')}
          <select
            value={sort}
            onChange={(event) => setSort(event.target.value as BookingListSort)}
            className="rounded-lg border border-borderDefault bg-bgBase px-3 py-1.5 text-sm text-textDefault"
          >
            <option value="start-asc">{t('bookingWidget.sortStartAsc')}</option>
            <option value="start-desc">{t('bookingWidget.sortStartDesc')}</option>
          </select>
        </label>
      </div>
      {visible.length === 0 ? (
        <div className="py-4 text-sm text-textSecondary">
          {tab === 'current' ? t('bookingWidget.noMoreBookings') : t('bookingWidget.noHistory')}
        </div>
      ) : (
        <ul className={`flex flex-col divide-y divide-borderDefault ${listClassName}`}>
          {visible.map((item) => (
            <li key={item.id}>
              <BookingRow
                item={item}
                locale={locale}
                todayLabel={t('bookingWidget.today')}
                timeZone={timeZone}
                vehicleName={vehicleName}
                statusLabel={statusLabel}
                editLabel={t('bookingWidget.edit')}
                cancelLabel={cancellingId === item.id ? t('common.loading') : t('booking.cancelBookingButton')}
                cancelling={cancellingId === item.id}
                onOpen={openBooking}
                onEdit={(row) => openBooking(row, 'edit')}
                onCancel={(row) => void cancelBooking(row)}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default MyBookingsList;
